import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@dopomogai/ui';
import { motion } from 'framer-motion';
import { HeartHandshake, ArrowRight, Sprout, Coins } from 'lucide-react';

export type ImpactParitySectionProps = {
  onAction?: (actionId: string) => void;
};

export const ImpactParitySection: React.FC<ImpactParitySectionProps> = ({
  onAction
}) => {
  const { t } = useTranslation('homepage');

  return (
    <section className="bg-slate-900 py-20 md:py-28 relative overflow-hidden text-white border-t border-slate-800">
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
         <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-emerald-500/10 blur-[120px] rounded-full" />
         <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-violet-500/10 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* LEFT: The Visual */}
          <div className="relative">
             <motion.div
               initial={{ opacity: 0, scale: 0.95 }}
               whileInView={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.8 }}
               className="relative rounded-2xl overflow-hidden border-2 border-slate-700 shadow-2xl bg-slate-800"
             >
                <img 
                  src="/images/illustrations/impact-parity-no-text.avif" 
                  alt="Robot arm watering a plant with a coin" 
                  className="w-full h-auto"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement!.classList.add('min-h-[300px]', 'flex', 'items-center', 'justify-center');
                    e.currentTarget.parentElement!.innerHTML = '<span class="text-slate-500 font-bold">Impact Visual Placeholder</span>';
                  }}
                />
                
                {/* Live Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                   <div className="bg-slate-900/90 backdrop-blur-md border border-slate-600 p-4 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center border border-slate-600">
                            <Coins size={18} className="text-slate-400" />
                         </div>
                         <div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Net Revenue</div>
                            <div className="text-sm font-mono text-white">$10,000.00</div>
                         </div>
                      </div>
                      <div className="text-emerald-500 rotate-90 sm:rotate-0">
                         <ArrowRight size={20} className="animate-pulse" />
                      </div>
                      <div className="flex items-center gap-3 text-right">
                         <div>
                            <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Impact Fund (33%)</div>
                            <div className="text-sm font-mono text-white font-bold">+$3,300.00</div>
                         </div>
                         <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center border border-emerald-500/50">
                            <Sprout size={18} className="text-emerald-400" />
                         </div>
                      </div>
                   </div>
                </div>
             </motion.div>
          </div>

          {/* RIGHT: The Manifesto */}
          <div>
             <div className="mb-6">
                <Badge text="Structural Philanthropy" accentColor="emerald" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/50" />
             </div>

             <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-[1.1]">
               The Robot Pays Taxes.
             </h2>

             <p className="text-lg text-slate-300 mb-8 leading-relaxed font-medium">
               We believe that automation should fund abundance, not just profit. 
               <br /><br />
               <span className="text-white font-bold">The Pledge:</span> 33% of our net revenue from AI Usage is automatically routed to our Social Impact Fund.
             </p>

             <ul className="space-y-4 mb-10">
                {["Enterprise AI for Non-Profits", "Disaster Relief Infrastructure", "Open Source Research Grants"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-200">
                     <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                        <HeartHandshake size={14} className="text-slate-900" />
                     </div>
                     <span className="font-medium">{item}</span>
                  </li>
                ))}
             </ul>

             {/* UPDATED: !important classes to force white styling on dark background */}
             <Button 
               variant="outline" 
               className="!bg-transparent !border-white !text-white hover:!bg-white hover:!text-slate-900 transition-all shadow-none hover:shadow-[4px_4px_0_0_rgba(255,255,255,0.3)]"
               text="Read the Vision"
               icon={<ArrowRight size={18} />}
               iconPosition="right"
               onClick={() => onAction?.('IMPACT_LEARN')}
             />
          </div>

        </div>
      </div>
    </section>
  );
};